import React, { useEffect, useState, useCallback } from 'react';
import classes from "./CreatePrediction2.module.css";
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import moment from "moment-timezone";
import {
    editUserPredictionsApi,
    fetchUpcomingMatchDetailsApi,
    getUserPredictionsApi,
    saveUserPredictionsApi,
} from '../../http/http';
import { FaArrowLeft, FaArrowRight, FaCheck, FaLock } from 'react-icons/fa6';
import { toast } from 'react-toastify';
import SmallLoader from '../../component/SmallLoader';
import { removeUserDetails } from '../../store/slices/userSlice';
import { useTranslation } from 'react-i18next';

/* ─── Step config ─── */
const GOAL_TIMES = [
    { label: "0 – 15 min", icon: "⚡", value: "0-15" },
    { label: "16 – 30 min", icon: "🔥", value: "16-30" },
    { label: "31 – 45 min", icon: "⏳", value: "31-45" },
    { label: "46 – 60 min", icon: "💥", value: "46-60" },
    { label: "61 – 75 min", icon: "🎯", value: "61-75" },
    { label: "76 – 90 min", icon: "🚀", value: "76-90" },
    { label: "No Goal", icon: "🚫", value: "none" },
];

/* ─── HT options — objects with full team info ─── */
const buildHtOptions = (
    homeTeamId, homeTeamName, homeTeamImage,
    awayTeamId, awayTeamName, awayTeamImage
) => {
    const opts = [];
    for (let h = 0; h <= 4; h++)
        for (let a = 0; a <= 4; a++)
            opts.push({
                homeTeamId,
                homeTeamName,
                homeTeamImage,
                homeTeamScore: h,
                awayTeamId,
                awayTeamName,
                awayTeamImage,
                awayTeamScore: a,
                key: `${h}-${a}`,
                label: `${h} – ${a}`,
            });
    return opts;
};

/* ─── helpers ─── */
const safeParse = (raw, fb = {}) => {
    try {
        const p = typeof raw === 'string' ? JSON.parse(raw) : raw;
        return (p && typeof p === 'object') ? p : fb;
    } catch { return fb; }
};
const safeArr = (v) => Array.isArray(v) ? v : [];
const POS_KEYS = ['attackers', 'midfielders', 'defenders', 'goalkeepers'];
const POS_LABELS = { attackers: 'FWD', midfielders: 'MID', defenders: 'DEF', goalkeepers: 'GK' };
const normTeam = (raw) => {
    const p = safeParse(raw, {});
    return POS_KEYS.reduce((a, k) => ({ ...a, [k]: safeArr(p[k]) }), {});
};

/* ─── Avatar ─── */
const Avatar = ({ player, size = 60, selected }) => {
    const [err, setErr] = useState(false);
    return (
        <div
            className={classes.avatar}
            style={{ width: size, height: size }}
            data-sel={selected || undefined}
        >
            {player.photo && !err
                ? <img src={player.photo} alt={player.name} className={classes.avatarImg} onError={() => setErr(true)} />
                : <span className={classes.avatarNum}>{player.number ?? '?'}</span>
            }
            {selected && <span className={classes.avatarTick}><FaCheck /></span>}
        </div>
    );
};

/* ════════════════════════════════════
   COMPONENT
════════════════════════════════════ */
const CreatePrediction2 = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { userDetails } = useSelector(s => s.persisted.userSlice);

    const { t } = useTranslation();

    const { selectedLanguage } = useSelector((state) => state.languageSlice);

    const STEPS = [
        { id: 0, emoji: "⚽", label: t("272"), sub: t("276") },
        { id: 1, emoji: "⏱", label: t("273"), sub: t("277") },
        { id: 2, emoji: "🔔", label: t("274"), sub: t("278") },
        { id: 3, emoji: "🏆", label: t("275"), sub: t("279") },
    ];

    const [homeData, setHomeData] = useState(normTeam(null));
    const [awayData, setAwayData] = useState(normTeam(null));
    const [matchDetails, setMatchDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [time, setTime] = useState(null);

    const [scorers, setScorers] = useState([]);
    const [firstGoal, setFirstGoal] = useState(null);
    const [halfTime, setHalfTime] = useState(null);  // stores full object: {homeTeamId,homeTeamName,homeTeamScore,awayTeamId,awayTeamName,awayTeamScore,key,label}
    const [winner, setWinner] = useState(null);   // { teamId, teamName }

    const [step, setStep] = useState(0);
    const [activeTeam, setActiveTeam] = useState(0);   // 0 = home, 1 = away tab

    /* ── countdown timer ── */
    const tick = useCallback(() => {
        if (!matchDetails?.match_date_time) return;
        const d = moment.duration(moment.utc(matchDetails.match_date_time).diff(moment()));
        if (d.asMilliseconds() <= 0) {
            navigate('/home');
            toast.info(t("144"));
            return;
        }
        setTime(`${Math.floor(d.asHours())}h ${d.minutes()}m ${d.seconds()}s`);
    }, [matchDetails]);

    useEffect(() => {
        const id = setInterval(tick, 1000);
        return () => clearInterval(id);
    }, [tick]);

    /* ── fetch match ── */
    const fetchMatch = async (id, show) => {
        try {
            if (show) setLoading(true);
            const r = await fetchUpcomingMatchDetailsApi(id, userDetails?.token);
            setMatchDetails(r?.data);
            setHomeData(normTeam(r?.data?.home_team_players));
            setAwayData(normTeam(r?.data?.away_team_players));
        } catch (e) {
            if (e?.response?.status === 403) {
                dispatch(removeUserDetails());
                toast.error(t("71"));
                navigate('/');
            } else {
                toast.error(e?.response?.data?.message || t("2"));
            }
        } finally {
            setLoading(false);
        }
    };

    /* ── fetch existing predictions (edit mode) ── */
    const fetchExisting = async (id) => {
        try {
            const r = await getUserPredictionsApi(id, userDetails?.token);
            safeArr(safeParse(r?.data?.[0]?.predictions, [])).forEach(p => {
                if (p.scorers) setScorers(safeArr(p.scorers));
                if (p.firstGoal) setFirstGoal(p.firstGoal);
                if (p.halfTime) setHalfTime(p.halfTime);
                if (p.winner) setWinner(p.winner);
            });
        } catch (e) { console.log(e); }
    };

    useEffect(() => {
        const id = location?.state?.match_fixture_id;
        if (!id) { navigate('/home'); return; }
        fetchMatch(id, true);
        if (location?.state?.edit) fetchExisting(id);
    }, [location?.state?.match_fixture_id]);

    /* ── scorer toggle ── */
    const toggle = (player) => setScorers(prev => {
        if (prev.find(p => p.id === player.id)) return prev.filter(p => p.id !== player.id);
        if (prev.length >= 3) { toast.info(t("145")); return prev; }
        return [...prev, player];
    });

    /* ── submit ── */
    const submit = async () => {
        if (!allDone) { toast.error(t("146")); return; }
        try {
            setSubmitting(true);
            const preds = [{ scorers }, { firstGoal }, { halfTime }, { winner }];
            const m = matchDetails;
            const a = [
                m.match_fixture_id, preds,
                m.match_date_time, m.match_venue_name, m.match_venue_city,
                m.league_id, m.league_name, m.league_image,
                m.home_team_id, m.home_team_name, m.home_team_image,
                m.away_team_id, m.away_team_name, m.away_team_image,
                userDetails?.token,
            ];
            location?.state?.edit
                ? await editUserPredictionsApi(...a)
                : await saveUserPredictionsApi(...a);
            toast.success(t("147"));
            navigate('/prediction-game', { state: { match_fixture_id: m.match_fixture_id } });
        } catch (e) {
            if (e?.response?.status === 403) {
                dispatch(removeUserDetails());
                toast.error(t("71"));
                navigate('/');
            } else {
                toast.error(e?.response?.data?.message || t("2"));
            }
        } finally {
            setSubmitting(false);
        }
    };

    /* ── derived ── */
    const stepDone = [scorers.length === 3, !!firstGoal, !!halfTime, !!winner];
    const allDone = stepDone.every(Boolean);
    const canNext = stepDone[step];
    const isLast = step === 3;

    /* HT options built with real team data once matchDetails is ready */
    const htOptions = matchDetails
        ? buildHtOptions(
            matchDetails.home_team_id, matchDetails.home_team_name, matchDetails.home_team_image,
            matchDetails.away_team_id, matchDetails.away_team_name, matchDetails.away_team_image,
        )
        : [];

    const buildSections = (data) =>
        POS_KEYS
            .map(k => ({ key: k, label: POS_LABELS[k], list: safeArr(data[k]) }))
            .filter(s => s.list.length > 0);

    const homeSections = buildSections(homeData);
    const awaySections = buildSections(awayData);
    const activeSections = activeTeam === 0 ? homeSections : awaySections;

    if (loading) return (
        <div className={classes.page}>
            <div className={classes.loaderScreen}><SmallLoader /></div>
        </div>
    );

    return (
        <div className={classes.page}>

            {/* ══ TOP NAV ══ */}
            <div className={classes.topNav}>
                <button className={classes.backBtn} onClick={() => navigate('/home')} aria-label="Back">
                    <FaArrowLeft />
                </button>
                <div className={classes.topNavMid}>
                    <span className={classes.topNavTitle}>{t("148")} &amp; {t("149")}</span>
                    {time && (
                        <span className={classes.timerChip}>
                            <span className={classes.timerDot} />
                            {time}
                        </span>
                    )}
                </div>
                <div className={classes.stepBubble}>
                    {step + 1}<span>/4</span>
                </div>
            </div>

            {/* ══ MATCH CARD ══ */}
            <div className={classes.matchCard}>
                <div className={classes.matchCardGlow} />
                <div className={classes.matchTeamCol}>
                    <div className={classes.matchLogoWrap}>
                        <img src={matchDetails?.home_team_image} alt="" className={classes.matchLogoImg} />
                    </div>
                    <span className={classes.matchTeamName}>{matchDetails?.home_team_name}</span>
                </div>
                <div className={classes.matchMidCol}>
                    <span className={classes.matchLeagueName}>{matchDetails?.league_name}</span>
                    <span className={classes.matchVs}>{t("150")}</span>
                    <span className={classes.matchKo}>
                        {moment.utc(matchDetails?.match_date_time).local().format('ddd DD MMM')}
                    </span>
                    <span className={classes.matchKoTime}>
                        {moment.utc(matchDetails?.match_date_time).local().format('HH:mm')}
                    </span>
                </div>
                <div className={classes.matchTeamCol}>
                    <div className={classes.matchLogoWrap}>
                        <img src={matchDetails?.away_team_image} alt="" className={classes.matchLogoImg} />
                    </div>
                    <span className={classes.matchTeamName}>{matchDetails?.away_team_name}</span>
                </div>
            </div>

            {/* ══ STEP TITLE BAR ══ */}
            <div className={classes.stepTitleBar}>
                <div className={classes.stepTitleLeft}>
                    <span className={classes.stepTitleEmoji}>{STEPS[step].emoji}</span>
                    <div>
                        <p className={classes.stepTitleText}>{STEPS[step].label}</p>
                        <p className={classes.stepTitleSub}>{STEPS[step].sub}</p>
                    </div>
                </div>
                {step === 0 && (
                    <div className={classes.scorerCounter}>
                        <span className={classes.scorerCountNum}>{scorers.length}</span>
                        <span className={classes.scorerCountOf}>/3</span>
                    </div>
                )}
                {stepDone[step] && step !== 0 && (
                    <span className={classes.stepDoneBadge}><FaCheck /> {t("151")}</span>
                )}
            </div>

            {/* ══ SCROLL CONTENT ══ */}
            <div className={classes.scrollBody}>

                {/* ─── STEP 0: Goal Scorers ─── */}
                {step === 0 && (
                    <div className={classes.panel}>

                        {/* 3 picked slots */}
                        <div className={classes.slotsRow}>
                            {[0, 1, 2].map(i => (
                                <div
                                    key={i}
                                    className={`${classes.slot} ${scorers[i] ? classes.slotFilled : ''}`}
                                >
                                    {scorers[i] ? (
                                        <>
                                            <Avatar player={scorers[i]} size={44} selected />
                                            <span className={classes.slotName}>
                                                {(scorers[i].name ?? '').split(' ').pop()}
                                            </span>
                                            <button className={classes.slotRemove} onClick={() => toggle(scorers[i])}>
                                                ✕
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <div className={classes.slotEmpty}>
                                                <span className={classes.slotEmptyNum}>{i + 1}</span>
                                            </div>
                                            <span className={classes.slotEmptyLabel}>{t("152")}</span>
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>

                        {/* team tabs */}
                        {(homeSections.length > 0 || awaySections.length > 0) && (
                            <div className={classes.teamTabs}>
                                {[
                                    { label: matchDetails?.home_team_name, logo: matchDetails?.home_team_image, idx: 0 },
                                    { label: matchDetails?.away_team_name, logo: matchDetails?.away_team_image, idx: 1 },
                                ].map(t => (
                                    <button
                                        key={t.idx}
                                        type="button"
                                        className={`${classes.teamTab} ${activeTeam === t.idx ? classes.teamTabActive : ''}`}
                                        onClick={() => setActiveTeam(t.idx)}
                                    >
                                        <img src={t.logo} alt="" className={classes.teamTabLogo} />
                                        <span>{t.label}</span>
                                    </button>
                                ))}
                            </div>
                        )}

                        {/* players by position */}
                        {activeSections.length === 0 && (
                            <div className={classes.emptyMsg}>
                                <span>⚽</span><p>{t("153")}</p>
                            </div>
                        )}

                        {activeSections.map(sec => (
                            <div key={sec.key} className={classes.posBlock}>
                                <div className={classes.posLabel}>
                                    <span className={classes.posBadge}>{sec.label}</span>
                                    <div className={classes.posDivider} />
                                </div>
                                <div className={classes.playerGrid}>
                                    {sec.list.map(p => {
                                        const sel = !!scorers.find(s => s.id === p.id);
                                        const idx = scorers.findIndex(s => s.id === p.id);
                                        return (
                                            <button
                                                key={p.id}
                                                type="button"
                                                className={`${classes.playerCard} ${sel ? classes.playerCardSel : ''}`}
                                                onClick={() => toggle({ ...p })}
                                            >
                                                {sel && (
                                                    <span className={classes.playerBadgeNum}>{idx + 1}</span>
                                                )}
                                                <Avatar player={p} size={58} selected={sel} />
                                                <span className={classes.playerName}>
                                                    {(p.name ?? '').split(' ').pop()}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* ─── STEP 1: 1st Goal Time ─── */}
                {step === 1 && (
                    <div className={classes.panel}>
                        <div className={classes.timeList}>
                            {GOAL_TIMES.map(opt => {
                                const sel = firstGoal === opt.value;
                                return (
                                    <button
                                        key={opt.value}
                                        type="button"
                                        className={`${classes.timeRow} ${sel ? classes.timeRowSel : ''}`}
                                        onClick={() => setFirstGoal(opt.value)}
                                    >
                                        <span className={classes.timeRowIcon}>{opt.icon}</span>
                                        <span className={classes.timeRowLabel}>{opt.label}</span>
                                        <span className={`${classes.timeRowCheck} ${sel ? classes.timeRowCheckVisible : ''}`}>
                                            <FaCheck />
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* ─── STEP 2: Halftime Score ─── */}
                {step === 2 && (
                    <div className={classes.panel}>

                        {/* context strip */}
                        <div className={classes.htStrip}>
                            <div className={classes.htStripTeam}>
                                <img src={matchDetails?.home_team_image} alt="" className={classes.htStripLogo} />
                                <span>{matchDetails?.home_team_name}</span>
                            </div>
                            <span className={classes.htStripVs}>{t("154")}</span>
                            <div className={`${classes.htStripTeam} ${classes.htStripRight}`}>
                                <img src={matchDetails?.away_team_image} alt="" className={classes.htStripLogo} />
                                <span>{matchDetails?.away_team_name}</span>
                            </div>
                        </div>

                        {/* score grid — shows team logos + numbers */}
                        <div className={classes.htGrid}>
                            {htOptions.map(opt => {
                                const sel = halfTime?.key === opt.key;
                                return (
                                    <button
                                        key={opt.key}
                                        type="button"
                                        className={`${classes.htCard} ${sel ? classes.htCardSel : ''}`}
                                        onClick={() => setHalfTime({
                                            homeTeamId: opt.homeTeamId,
                                            homeTeamName: opt.homeTeamName,
                                            homeTeamScore: opt.homeTeamScore,
                                            awayTeamId: opt.awayTeamId,
                                            awayTeamName: opt.awayTeamName,
                                            awayTeamScore: opt.awayTeamScore,
                                            key: opt.key,
                                            label: opt.label,
                                        })}
                                    >
                                        {sel && <FaCheck className={classes.htCheck} />}

                                        {/* home logo · home score  –  away score · away logo */}
                                        <div className={classes.htCardInner}>
                                            <div className={classes.htCardSide}>
                                                <img
                                                    src={opt.homeTeamImage}
                                                    alt={opt.homeTeamName}
                                                    className={classes.htCardLogo}
                                                />
                                                <span className={classes.htCardScore}>
                                                    {opt.homeTeamScore}
                                                </span>
                                            </div>

                                            <span className={classes.htCardDash}>–</span>

                                            <div className={classes.htCardSide}>
                                                <span className={classes.htCardScore}>
                                                    {opt.awayTeamScore}
                                                </span>
                                                <img
                                                    src={opt.awayTeamImage}
                                                    alt={opt.awayTeamName}
                                                    className={classes.htCardLogo}
                                                />
                                            </div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* ─── STEP 3: Match Winner ─── */}
                {step === 3 && (
                    <div className={classes.panel}>
                        <div className={classes.winnerGrid}>
                            {[
                                {
                                    label: matchDetails?.home_team_name,
                                    img: matchDetails?.home_team_image,
                                    val: matchDetails?.home_team_name,
                                    teamId: matchDetails?.home_team_id,
                                    tag: t("155"),
                                },
                                {
                                    label: 'Draw',
                                    img: null,
                                    val: 'Draw',
                                    teamId: null,
                                    tag: t("156"),
                                },
                                {
                                    label: matchDetails?.away_team_name,
                                    img: matchDetails?.away_team_image,
                                    val: matchDetails?.away_team_name,
                                    teamId: matchDetails?.away_team_id,
                                    tag: t("157"),
                                },
                            ].map(opt => {
                                const sel = winner?.teamName === opt.val;
                                return (
                                    <button
                                        key={opt.val}
                                        type="button"
                                        className={`${classes.winCard} ${sel ? classes.winCardSel : ''}`}
                                        onClick={() => setWinner({ teamId: opt.teamId, teamName: opt.val })}
                                    >
                                        {sel && (
                                            <span className={classes.winCardTick}><FaCheck /></span>
                                        )}
                                        <span className={classes.winCardTag}>{opt.tag}</span>
                                        <div className={classes.winCardLogoWrap}>
                                            {opt.img
                                                ? <img src={opt.img} alt="" className={classes.winCardLogo} />
                                                : <span className={classes.winDrawIcon}>🤝</span>
                                            }
                                        </div>
                                        <span className={classes.winCardName}>{opt.label}</span>
                                    </button>
                                );
                            })}
                        </div>

                        {/* summary when all steps done */}
                        {allDone && (
                            <div className={classes.summary}>
                                <p className={classes.summaryTitle}>{t("158")}</p>
                                {[
                                    {
                                        k: t("159"),
                                        v: scorers.map(s => (s.name ?? '').split(' ').pop()).join('  ·  '),
                                    },
                                    {
                                        k: t("160"),
                                        v: GOAL_TIMES.find(o => o.value === firstGoal)?.label ?? firstGoal,
                                    },
                                    { k: t("161"), v: halfTime?.label },
                                    { k: t("162"), v: winner?.teamName },
                                ].map(row => (
                                    <div key={row.k} className={classes.summaryRow}>
                                        <span className={classes.summaryK}>{row.k}</span>
                                        <span className={classes.summaryV}>{row.v}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

            </div>

            {/* ══ FOOTER ══ */}
            <div className={classes.footer}>
                <div className={classes.progressTrack}>
                    <div
                        className={classes.progressFill}
                        style={{ width: `${(step / 4) * 100}%` }}
                    />
                </div>

                <div className={classes.footerRow}>
                    {step > 0 ? (
                        <button
                            type="button"
                            className={classes.btnSecondary}
                            onClick={() => setStep(s => s - 1)}
                        >
                            <FaArrowLeft className={classes.btnIcon} />
                            {t("163")}
                        </button>
                    ) : <div />}

                    {isLast ? (
                        <button
                            type="button"
                            disabled={!allDone || submitting}
                            className={`${classes.btnPrimary} ${allDone ? classes.btnGold : classes.btnDisabled}`}
                            onClick={submit}
                        >
                            {submitting
                                ? <SmallLoader style={{ width: 18, height: 18, border: '3px solid #1a0000', borderTopColor: 'transparent' }} />
                                : <><FaLock className={classes.btnIcon} />{location?.state?.edit ? t("164") : t("165")}</>
                            }
                        </button>
                    ) : (
                        <button
                            type="button"
                            disabled={!canNext}
                            className={`${classes.btnPrimary} ${canNext ? classes.btnRed : classes.btnDisabled}`}
                            onClick={() => setStep(s => s + 1)}
                        >
                            {t("166")} ({step + 1}/4)
                            <FaArrowRight className={classes.btnIcon} />
                        </button>
                    )}
                </div>
            </div>

        </div>
    );
};

export default CreatePrediction2;